import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ArrowLeft, ArrowRight } from "lucide-react";
import { Stars } from "@/components/ReviewForm";

export function TestimonialCarousel({ items = [], testId = "testimonial-carousel", auto = true }) {
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const n = items.length;

  const go = useCallback((d) => { setDir(d); setI((p) => (p + d + n) % n); }, [n]);

  useEffect(() => {
    if (!auto || n <= 1) return;
    const t = setInterval(() => go(1), 6000);
    return () => clearInterval(t);
  }, [auto, n, go]);

  if (!n) return null;
  const t = items[i];

  return (
    <div data-testid={testId} className="relative">
      <Quote className="text-electric/30 mx-auto mb-6" size={56} strokeWidth={1.5} />
      <div className="relative min-h-[220px] sm:min-h-[200px] flex items-center justify-center">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.blockquote
            key={i}
            custom={dir}
            initial={{ opacity: 0, x: dir * 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -40 }}
            transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
            className="text-center max-w-4xl mx-auto px-2"
          >
            <p className="font-display text-2xl sm:text-3xl md:text-4xl font-medium leading-snug text-ice">
              "{t.quote}"
            </p>
            {t.rating ? <div className="flex justify-center mt-6"><Stars value={t.rating} size={18} /></div> : null}
            <cite className={`label not-italic ${t.rating ? "mt-4" : "mt-8"} block text-cyan`}>— {t.author}</cite>
          </motion.blockquote>
        </AnimatePresence>
      </div>

      {n > 1 && (
        <div className="flex items-center justify-center gap-6 mt-8">
          <button data-testid="testimonial-prev" aria-label="Previous testimonial" onClick={() => go(-1)}
            className="h-11 w-11 rounded-full glass flex items-center justify-center text-ice hover:text-cyan hover:border-cyan/40 transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div className="flex gap-2">
            {items.map((_, idx) => (
              <button key={idx} aria-label={`Testimonial ${idx + 1}`} onClick={() => { setDir(idx > i ? 1 : -1); setI(idx); }}
                className={`h-1.5 rounded-full transition-all duration-500 ${idx === i ? "w-8 bg-cyan" : "w-2 bg-white/20"}`} />
            ))}
          </div>
          <button data-testid="testimonial-next" aria-label="Next testimonial" onClick={() => go(1)}
            className="h-11 w-11 rounded-full glass flex items-center justify-center text-ice hover:text-cyan hover:border-cyan/40 transition-colors">
            <ArrowRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
}

export default TestimonialCarousel;
